/**
 * Conversation list component.
 * Displays all user's conversations with unread counts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Users } from 'lucide-react';
import { ChatRoom } from '../../services/chat';
import chatService from '../../services/chat';
import { formatRelativeTime } from '../../utils/dateUtils';
import AvatarDisplay from '../../components/ui/AvatarDisplay';
import { useAuthStore } from '../../stores/authStore';
import { useChatParticipants } from '../../hooks/useChatParticipants';

interface ConversationListProps {
  rooms: ChatRoom[];
  selectedRoom: ChatRoom | null;
  onSelectRoom: (room: ChatRoom) => void;
  loading: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  rooms,
  selectedRoom,
  onSelectRoom,
  loading,
}) => {
  const [onlineUsers, setOnlineUsers] = useState<number[]>([]);

  // Fetch online users
  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await chatService.getOnlineUsers();
      setOnlineUsers(response.online_users);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  }, []);

  // Fetch online users on mount and periodically
  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [fetchOnlineUsers]);
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-gray-500 dark:text-gray-400 text-center">
          Aucune conversation pour le moment
        </p>
      </div>
    );
  }

  const ConversationListItem: React.FC<{ room: ChatRoom; isSelected: boolean; onClick: () => void; }> = ({ room, isSelected, onClick }) => {
    const hasUnread = room.unread_count && room.unread_count > 0;
    const { user: currentUser } = useAuthStore();
    const { getParticipantName, getParticipantAvatar } = useChatParticipants(room);

    // Résoudre destinataire pour DIRECT
    const resolveDirectDisplay = () => {
      if (room.type !== 'DIRECT' || !room.participants || room.participants.length === 0) {
        // Priorité aux champs backend si fournis
        if (room.type === 'DIRECT' && (room as any).peer_display_name) {
          return { name: (room as any).peer_display_name as string, avatar: (room as any).peer_avatar_url as string | undefined };
        }
        return { name: room.name || 'Conversation directe', avatar: (room as any).peer_avatar_url || room.avatar_url };
      }
      // Si backend fournit déjà le nom/avater, les utiliser directement
      if ((room as any).peer_display_name) {
        return { name: (room as any).peer_display_name as string, avatar: (room as any).peer_avatar_url as string | undefined };
      }
      const myId = currentUser?.id;
      const myRole = currentUser?.role;
      const meMatcher = (p: any) => {
        if (!myId) return false;
        if (myRole === 'client') return p.client_id === myId;
        if (myRole === 'employee') return p.employee_id === myId;
        return p.user_id === myId; // admin/super_admin/user
      };
      const other = room.participants.find((p: any) => !meMatcher(p));
      if (!other) return { name: room.name || 'Conversation directe', avatar: room.avatar_url };
      const name = getParticipantName(other.user_id, other.employee_id, other.client_id);
      const avatar = getParticipantAvatar(other.user_id, other.employee_id, other.client_id);
      return { name, avatar };
    };

    const display = resolveDirectDisplay();

    return (
      <button
        onClick={onClick}
        className={`w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left active:bg-gray-100 dark:active:bg-gray-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
          }`}
      >
        <div className="flex items-center space-x-3">
          {/* Avatar - Facebook style */}
          <div className="flex-shrink-0 relative">
            {room.type === 'GROUP' ? (
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                <Users className="h-7 w-7 text-white" />
              </div>
            ) : (
              (() => {
                // Determine if the other participant is online
                const otherParticipant = room.participants?.find(p =>
                  (p.user_id !== currentUser?.id) ||
                  (p.employee_id !== currentUser?.id) ||
                  (p.client_id !== currentUser?.id)
                );
                const otherUserId = otherParticipant?.user_id || otherParticipant?.employee_id || otherParticipant?.client_id;
                const isOtherUserOnline = otherUserId ? onlineUsers.includes(otherUserId) : false;

                return (
                  <div className="relative">
                    <AvatarDisplay
                      avatarUrl={display.avatar}
                      name={display.name}
                      size="w-14 h-14"
                      showOnlineStatus={false}
                      isOnline={isOtherUserOnline}
                    />
                    {/* Facebook-style online indicator */}
                    {isOtherUserOnline && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          {/* Content - Facebook style */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className={`text-base font-semibold truncate ${hasUnread ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'
                }`}>
                {room.type === 'DIRECT' ? display.name : (room.peer_display_name || room.name || 'Groupe')}
              </h3>

              {room.last_message_at && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0 font-medium">
                  {formatRelativeTime(room.last_message_at)}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <p className={`text-sm truncate ${hasUnread ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                  {room.last_message_preview || 'Aucun message'}
                </p>
                {room.is_muted && (
                  <span className="ml-2 text-gray-400 dark:text-gray-500 flex-shrink-0">🔕</span>
                )}
              </div>

              {hasUnread && (
                <span className="flex-shrink-0 ml-3 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {(room.unread_count || 0) > 99 ? '99+' : (room.unread_count || 0)}
                </span>
              )}
            </div>

            {/* Group participants indicator */}
            {room.type === 'GROUP' && room.participants && (
              <div className="mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {room.participants.length} participants
                </span>
              </div>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
      {rooms.map((room, index) => {
        const isSelected = selectedRoom?.id === room.id;
        return (
          <div key={room.id}>
            <ConversationListItem
              room={room}
              isSelected={isSelected}
              onClick={() => onSelectRoom(room)}
            />
            {/* Separator line except for last item */}
            {index < rooms.length - 1 && (
              <div className="mx-4 border-b border-gray-100 dark:border-gray-800"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
